import type { RemoteSource } from '../../components/ui/SearchableSelect';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * AJAX dropdown sources. Form fields use the ACTIVE-only variants (new data
 * must not reference inactive masters); list filters include inactive rows so
 * history can still be filtered.
 */
const active = { status: 'ACTIVE' };

const app = (params: object = active): RemoteSource => ({
  resource: 'apps',
  params: params as RemoteSource['params'],
  label: (r: any) => r.name,
  hint: (r: any) => [r.developerName, r.packageName].filter(Boolean).join(' · '),
  icon: (r: any) => r.iconUrl ?? null,
});

const platform = (params: object = active): RemoteSource => ({
  resource: 'advertising-platforms',
  params: params as RemoteSource['params'],
  label: (r: any) => r.name,
});

const gstAccount = (params: object = active): RemoteSource => ({
  resource: 'gst-accounts',
  params: params as RemoteSource['params'],
  label: (r: any) => r.tradeName || r.legalName,
  hint: (r: any) => `${r.gstin} · ${r.state}`,
});

const currency = (params: object = active): RemoteSource => ({
  resource: 'currencies',
  valueKey: 'code',
  params: params as RemoteSource['params'],
  label: (r: any) => `${r.code} — ${r.name}`,
});

const revenueSource = (params: object = active): RemoteSource => ({
  resource: 'revenue-sources',
  params: params as RemoteSource['params'],
  label: (r: any) => r.name,
});

const managerAccount = (params: object = active): RemoteSource => ({
  resource: 'manager-accounts',
  params: params as RemoteSource['params'],
  label: (r: any) => r.name,
  hint: (r: any) =>
    [r.accountIdentifier, r.loginEmail, r.gstAccount?.gstin].filter(Boolean).join(' · '),
});

const usedBy = (r: any) =>
  r.assignments?.length ? `Used by ${r.assignments.map((x: any) => x.app.name).join(', ')}` : null;

const advertisingAccount = (params: object = active): RemoteSource => ({
  resource: 'advertising-accounts',
  params: params as RemoteSource['params'],
  label: (r: any) =>
    r.accountIdentifier ? `${r.accountName} (${r.accountIdentifier})` : r.accountName,
  hint: (r: any) => [r.platform?.name, r.loginEmail, usedBy(r)].filter(Boolean).join(' · '),
});

const revenueAccount = (params: object = active): RemoteSource => ({
  resource: 'revenue-accounts',
  params: params as RemoteSource['params'],
  label: (r: any) => r.accountName,
  hint: (r: any) =>
    [r.revenueSource?.name, r.currencyCode, r.accountIdentifier, usedBy(r)]
      .filter(Boolean)
      .join(' · '),
});

const expenseCategory = (params: object = active): RemoteSource => ({
  resource: 'expense-categories',
  params: params as RemoteSource['params'],
  label: (r: any) => r.name,
});

const firebaseAccount = (params: object = active): RemoteSource => ({
  resource: 'firebase-accounts',
  params: params as RemoteSource['params'],
  label: (r: any) => r.name,
  hint: (r: any) => [r.loginEmail, r.accessEmail].filter(Boolean).join(' · '),
});

const playDeveloperAccount = (params: object = active): RemoteSource => ({
  resource: 'play-developer-accounts',
  params: params as RemoteSource['params'],
  label: (r: any) => r.name,
  hint: (r: any) => [r.developerId, r.loginEmail].filter(Boolean).join(' · '),
});

export const sources = {
  playDeveloperAccount,
  firebaseAccount,
  expenseCategory,
  app,
  platform,
  gstAccount,
  currency,
  revenueSource,
  managerAccount,
  advertisingAccount,
  revenueAccount,
  all: {} as object,
};
